// components/SteinBild.js
// Wiederverwendbare Bild-Komponente für Stein-Fotos.
// Kapselt Bildanzeige, Fehlerbehandlung und Vorschau-Logik zentral,
// damit beim Multiple-Images-Feature nur diese Komponente angepasst werden muss.

import React, { useState, useEffect } from 'react';
import '../styles/SteinBild.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Props:
//   bildPfad   – String | null  – relativer Serverpfad des gespeicherten Bildes
//   bildDatei  – File | null    – lokal gewählte Datei (Bearbeitungsmodus, Vorschau)
//   onClick    – Function | null – Callback bei Klick auf das Bild
//   size       – 'card' | 'detail' | 'thumbnail'
//   alt        – String         – Alternativtext
function SteinBild({
  bildPfad = null,
  bildDatei = null,
  onClick = null,
  size = 'card',
  alt = 'Steinbild',
}) {
  // Blob-URL für die lokale Dateivorschau – wird sauber beim Unmount widerrufen.
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!bildDatei) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(bildDatei);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [bildDatei]);

  const handleError = (e) => {
    e.target.onerror = null;
    e.target.src = '/placeholder.png';
    e.target.alt = 'Bild nicht verfügbar';
    e.target.style.objectFit = 'contain';
  };

  // Cursor nur als pointer wenn tatsächlich ein Click-Handler vorhanden ist.
  // Verhindert Pointer-Cursor auf Detail-Bild in SteinDetail ohne Modal-Funktion.
  const cursorStyle = onClick ? { cursor: 'pointer' } : undefined;

  // Lazy loading nur für Karten – auf der Detailseite ist das Bild der Hauptinhalt
  // und soll sofort geladen werden.
  const loadingAttr = size === 'card' ? 'lazy' : 'eager';

  // 1. Lokale Datei-Vorschau (Bearbeitungsmodus)
  if (previewUrl) {
    return (
      <div className={`stein-bild-container size-${size}`}>
        <img
          src={previewUrl}
          alt="Vorschau neues Bild"
          className={`stein-bild-hauptbild size-${size}`}
          style={cursorStyle}
          onClick={onClick || undefined}
        />
      </div>
    );
  }

  // 2. Server-Bild
  if (bildPfad) {
    return (
      <div className={`stein-bild-container size-${size}`}>
        <img
          src={`${BASE_URL}/${bildPfad}`}
          alt={alt}
          className={`stein-bild-hauptbild size-${size}`}
          loading={loadingAttr}
          style={cursorStyle}
          onClick={onClick || undefined}
          onError={handleError}
        />
      </div>
    );
  }

  // 3. Kein Bild → Placeholder
  // Karten-Placeholder verhindert, dass der Klick auf die Karte navigiert.
  const placeholderClick = size === 'card' ? (e) => e.stopPropagation() : undefined;
  return (
    <div
      className={`stein-bild-container stein-bild-placeholder size-${size}`}
      onClick={placeholderClick}
    >
      <span>Kein Bild</span>
    </div>
  );
}

export default SteinBild;
